import { useState } from 'react';
import Modal from '../components/Modal.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { fmtCents, parseAmountToCents } from '../format.js';

export default function Recurring({ state, lookups, mutate, showToast, onBack }) {
  const [editing, setEditing] = useState(null);  // row being edited or 'new'
  const [showInactive, setShowInactive] = useState(false);

  const visible = state.recurring
    .filter(r => showInactive || r.active)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  return (
    <div className="screen">
      <BackHeader onBack={onBack} title="Recurring" />

      <div className="row between" style={{ marginBottom: 12 }}>
        <label className="tiny muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
                 style={{ width: 'auto' }} />
          Show inactive
        </label>
        <button className="primary" onClick={() => setEditing('new')}>+ New</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {visible.length === 0 ? (
          <div className="empty">
            <div className="icon">∅</div>
            <div>No recurring items yet</div>
          </div>
        ) : (
          visible.map(r => (
            <button key={r.id} onClick={() => setEditing(r)}
                    className="list-item"
                    style={{
                      width: '100%', textAlign: 'left', background: 'transparent',
                      border: 'none', borderBottom: '1px solid var(--border2)',
                      borderRadius: 0, opacity: r.active ? 1 : 0.5, cursor: 'pointer',
                    }}>
              <div className="grow">
                <div>{r.item}</div>
                <div className="meta">
                  {r.frequency}{r.anchor ? ` · ${r.anchor}` : ''} · {lookups.catName(r.category_id)} · {lookups.acctName(r.account_id)}
                </div>
              </div>
              <span className={'amount ' + (r.type === 'Income' ? 'pos' : 'neg')}>
                {r.type === 'Income' ? '+' : '−'}{fmtCents(r.amount_cents).replace('-', '')}
              </span>
            </button>
          ))
        )}
      </div>

      <RecurringEditor
        editing={editing}
        onClose={() => setEditing(null)}
        state={state}
        mutate={mutate}
        showToast={showToast}
      />
    </div>
  );
}

function BackHeader({ onBack, title }) {
  return (
    <div className="row" style={{ marginBottom: 16, gap: 12 }}>
      <button onClick={onBack} className="ghost" style={{ padding: '4px 8px' }}>‹</button>
      <h1 style={{ margin: 0 }}>{title}</h1>
    </div>
  );
}

const FREQUENCIES = ['Monthly', 'Weekly', 'BiWeekly', 'EveryNWeeks', 'EveryNMonths', 'Quarterly', 'Annual', 'PairedWithPaycheck', 'OneTime'];
const TYPES = ['Bill', 'Income', 'Savings', 'Sinking Drain', 'Discretionary', 'Other'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function RecurringEditor({ editing, onClose, state, mutate, showToast }) {
  const isNew = editing === 'new';
  const open = !!editing;
  const initial = isNew ? {
    item: '', amount_cents: 0, frequency: 'Monthly', every_n: 1, anchor: '1',
    months_active: 'All', account_id: '', category_id: '', type: 'Bill',
    starts_on: null, ends_on: null, active: 1, notes: '',
  } : (editing || {});

  // Have to reset form when `editing` changes — track via key on Modal children
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title={isNew ? 'New Recurring Item' : 'Edit Recurring'}>
      <RecurringForm
        key={isNew ? 'new' : editing.id}
        initial={initial}
        isNew={isNew}
        state={state}
        mutate={mutate}
        showToast={showToast}
        onClose={onClose}
      />
    </Modal>
  );
}

function RecurringForm({ initial, isNew, state, mutate, showToast, onClose }) {
  const ask = useConfirm();
  const [form, setForm] = useState({
    ...initial,
    amountInput: initial.amount_cents ? (Math.abs(initial.amount_cents) / 100).toFixed(2) : '',
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Anchor field changes meaning by frequency
  const anchorIsWeekday = ['Weekly', 'BiWeekly', 'EveryNWeeks'].includes(form.frequency);
  const showEveryN = ['EveryNMonths', 'EveryNWeeks'].includes(form.frequency);
  const showAnchor = !['PairedWithPaycheck', 'OneTime'].includes(form.frequency);

  async function save() {
    if (!form.item || !form.account_id || !form.category_id) {
      showToast({ kind: 'error', message: 'Item, category, and account are required.' });
      return;
    }
    const cents = parseAmountToCents(form.amountInput);
    if (cents == null || cents === 0) {
      showToast({ kind: 'error', message: 'Amount is required.' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        item: form.item.trim(),
        amount_cents: Math.abs(cents),
        frequency: form.frequency,
        every_n: parseInt(form.every_n, 10) || 1,
        anchor: form.anchor || null,
        months_active: form.months_active || 'All',
        account_id: form.account_id,
        category_id: form.category_id,
        type: form.type,
        starts_on: form.starts_on || null,
        ends_on: form.ends_on || null,
        active: form.active ? 1 : 0,
        notes: form.notes || '',
      };
      if (isNew) {
        await mutate.addRecurring(payload);
        showToast({ kind: 'success', message: 'Added: ' + payload.item });
      } else {
        await mutate.updateRecurring(initial.id, payload);
        showToast({ kind: 'success', message: 'Saved: ' + payload.item });
      }
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSaving(false); }
  }

  async function archive() {
    const ok = await ask({ title: 'Deactivate recurring item?', message: 'It will no longer appear in your forecast. You can re-enable it from the recurring list.', confirmLabel: 'Deactivate', danger: true }); if (!ok) return;
    setSaving(true);
    try {
      await mutate.deleteRecurring(initial.id);
      showToast({ kind: 'success', message: 'Deactivated.' });
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSaving(false); }
  }

  return (
    <>
      <div className="field">
        <label>Item</label>
        <input value={form.item} onChange={(e) => update('item', e.target.value)} placeholder="e.g. Mortgage" autoFocus />
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Type</label>
          <select value={form.type} onChange={(e) => update('type', e.target.value)}>
            {TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Amount</label>
          <input inputMode="decimal" value={form.amountInput}
                 onChange={(e) => update('amountInput', e.target.value)} placeholder="0.00" />
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Frequency</label>
          <select value={form.frequency} onChange={(e) => update('frequency', e.target.value)}>
            {FREQUENCIES.map(f => <option key={f}>{f}</option>)}
          </select>
        </div>
        {showEveryN && (
          <div className="field" style={{ flex: '0 0 90px' }}>
            <label>Every N</label>
            <input type="number" value={form.every_n} onChange={(e) => update('every_n', e.target.value)} min={1} />
          </div>
        )}
      </div>
      {showAnchor && (
        <div className="field">
          <label>{anchorIsWeekday ? 'Day of week' : 'Day of month'}</label>
          {anchorIsWeekday ? (
            <select value={form.anchor || ''} onChange={(e) => update('anchor', e.target.value)}>
              <option value="">Select…</option>
              {WEEKDAYS.map(d => <option key={d}>{d}</option>)}
            </select>
          ) : (
            <input type="number" min={1} max={31} value={form.anchor || ''} onChange={(e) => update('anchor', e.target.value)} />
          )}
        </div>
      )}
      <div className="field">
        <label>Months Active</label>
        <input value={form.months_active} onChange={(e) => update('months_active', e.target.value)}
               placeholder="All, or e.g. 'Apr-Oct' or 'Jan,Apr,Jul,Oct'" />
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Category</label>
          <select value={form.category_id} onChange={(e) => update('category_id', e.target.value)}>
            <option value="">Select…</option>
            {state.categories.filter(c => !c.archived).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Account</label>
          <select value={form.account_id} onChange={(e) => update('account_id', e.target.value)}>
            <option value="">Select…</option>
            {state.accounts.filter(a => !a.archived).map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Starts on</label>
          <input type="date" value={form.starts_on || ''}
                 onChange={(e) => update('starts_on', e.target.value || null)} />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Ends on</label>
          <input type="date" value={form.ends_on || ''}
                 onChange={(e) => update('ends_on', e.target.value || null)} />
        </div>
      </div>
      <div className="field">
        <label>Notes</label>
        <input value={form.notes} onChange={(e) => update('notes', e.target.value)} />
      </div>
      <div className="field">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none' }}>
          <input type="checkbox" checked={!!form.active} onChange={(e) => update('active', e.target.checked ? 1 : 0)}
                 style={{ width: 'auto' }} />
          <span>Active</span>
        </label>
      </div>

      <div className="row between" style={{ marginTop: 24 }}>
        {!isNew ? (
          <button onClick={archive} className="danger" disabled={saving}>Deactivate</button>
        ) : <span />}
        <div className="row" style={{ gap: 8 }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}
